import React, { useEffect, useRef, useState } from 'react';
import { Container } from '@fiverr-private/layout_components';
import { Typography } from '@fiverr-private/typography';
import { useBreakpoint } from '@fiverr-private/hooks';
import { PARTNER_REVEAL_FULL_TEXT, PARTNER_REVEAL_LINES } from './constants';
import {
  lineBase,
  lineItem,
  lineReveal,
  lineText,
  mobileParagraph,
  mobileRevealed,
  mobileUnrevealed,
  section,
  textWrap,
} from './styles.ve.css';

const clamp = (v: number, min: number, max: number): number => Math.min(Math.max(v, min), max);

const MOBILE_BREAKPOINTS = new Set(['default', 'sm']);

const PartnerRevealSection: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef(0);
  const [progress, setProgress] = useState(0);
  const breakpoint = useBreakpoint();
  const isMobile = MOBILE_BREAKPOINTS.has(breakpoint ?? 'default');

  useEffect(() => {
    const el = sectionRef.current;

    if (!el) {
      return;
    }

    const initialRect = el.getBoundingClientRect();
    const sectionInitialCenter = initialRect.top + window.scrollY + initialRect.height / 2;

    const updateProgress = () => {
      const rect = el.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const sectionCenter = rect.top + rect.height / 2;

      const startPoint = Math.min(viewportHeight * 0.85, sectionInitialCenter);
      const endPoint = viewportHeight * 0.35;
      const nextProgress = clamp((startPoint - sectionCenter) / (startPoint - endPoint), 0, 1);

      setProgress(nextProgress);
    };

    const onScroll = () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(updateProgress);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    updateProgress();

    return () => {
      window.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const renderMobile = () => {
    const charIndex = Math.round(progress * PARTNER_REVEAL_FULL_TEXT.length);

    return (
      <p className={mobileParagraph}>
        <span className={mobileRevealed}>{PARTNER_REVEAL_FULL_TEXT.slice(0, charIndex)}</span>
        <span className={mobileUnrevealed}>{PARTNER_REVEAL_FULL_TEXT.slice(charIndex)}</span>
      </p>
    );
  };

  const renderDesktop = () =>
    PARTNER_REVEAL_LINES.map((line, index) => {
      const lineProgress = clamp(progress * PARTNER_REVEAL_LINES.length - index, 0, 1);

      return (
        <Container key={line} className={lineItem}>
          <Typography className={`${lineText} ${lineBase}`} as="p">
            {line}
          </Typography>
          <Typography
            className={`${lineText} ${lineReveal}`}
            as="p"
            aria-hidden
            style={{ '--line-reveal': `${lineProgress * 100}%` } as React.CSSProperties}
          >
            {line}
          </Typography>
        </Container>
      );
    });

  return (
    <Container className={section} ref={sectionRef}>
      <Container className={textWrap}>{isMobile ? renderMobile() : renderDesktop()}</Container>
    </Container>
  );
};

export default PartnerRevealSection;
